import { AlertTriangle, ArrowUpRight, CheckCircle2, CircleSlash } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { BrandDetails } from '@/features/brands';
import { StatusBadge } from '@/shared/components/status-badge';
import { attentionLabel, attentionSeverity, reasonLabel } from '@/shared/lib/catalogue-labels';
import { cn } from '@/shared/lib/utils';

/*
 * Display only. Every state and reason below comes from the Admin catalogue API
 * (the backend's catalogue rules); this file picks which to show and names them.
 */

/** Codes that are a yes/no fact: no count is shown. */
const FLAGS = new Set([
  'BrandInactive',
  'CategoryInactive',
  'NoActiveDenominations',
  'CountryInactive',
  'CountryWithoutPricing',
]);

interface Issue {
  key: string;
  label: string;
  count: number | null;
  severity: 'danger' | 'warning';
}

/** The brand's issues that affect this country and can stop or change a sale. */
function brandIssues(details: BrandDetails, countryId: number): Issue[] {
  const relevant = details.attention.filter(
    (item) => item.countryId === null || item.countryId === countryId,
  );
  const notSellable = relevant.some((item) => item.code === 'NotSellableInCountry');
  return (
    relevant
      // "Every provider is blocked" is one cause of "customers cannot buy": listed once.
      .filter((item) => !(item.code === 'CostSourceBlockedInCountry' && notSellable))
      .flatMap((item): Issue[] => {
        const severity = attentionSeverity(item.code);
        if (severity !== 'danger' && severity !== 'warning') return [];
        return [
          {
            key: `${item.code}-${item.countryId ?? 'brand'}`,
            label: attentionLabel(item.code),
            count: FLAGS.has(item.code) ? null : item.count,
            severity,
          },
        ];
      })
      .sort((a, b) => Number(b.severity === 'danger') - Number(a.severity === 'danger'))
  );
}

function Callout({
  tone,
  title,
  action,
  children,
}: {
  tone: 'danger' | 'warning';
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const Icon = tone === 'danger' ? CircleSlash : AlertTriangle;
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-md border p-3 text-xs',
        tone === 'danger'
          ? 'border-status-danger-border bg-status-danger-bg'
          : 'border-status-warning-border bg-status-warning-bg',
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 size-4 shrink-0',
          tone === 'danger' ? 'text-status-danger' : 'text-status-warning',
        )}
        aria-hidden
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-foreground font-medium">{title}</p>
          {action}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * What the catalogue already says about the chosen brand and denomination in
 * this country — shown as soon as they are picked, so an item checkout cannot
 * sell is flagged before (and beside) the engine's answer.
 */
export function SelectionChecks({
  details,
  countryId,
  countryLabel,
  denominationId,
}: {
  details: BrandDetails;
  countryId: number;
  countryLabel: string;
  denominationId: number | null;
}) {
  const issues = brandIssues(details, countryId);
  const denomination = details.denominations.find((d) => d.id === denominationId);
  const state = denomination?.countries.find((s) => s.countryId === countryId);
  const itemReasons = state
    ? [...state.visibilityReasons, ...state.sellabilityReasons].filter((r) => r !== 'NotVisible')
    : [];
  const itemBlocked = state !== undefined && (!state.offered || !state.sellable);
  const brandName = details.brand.name;

  const openBrand = (
    <Link
      to={`/brands/${details.brand.id}`}
      className="text-primary inline-flex shrink-0 items-center gap-0.5 font-medium underline-offset-2 hover:underline"
    >
      Open brand
      <ArrowUpRight className="size-3" aria-hidden />
    </Link>
  );

  if (issues.length === 0 && !itemBlocked && (state?.unusableProviders.length ?? 0) === 0) {
    return (
      <section
        aria-label="Catalogue checks"
        className="border-border text-status-success flex items-center gap-1.5 border-t px-4 py-2.5 text-xs"
      >
        <CheckCircle2 className="size-3.5" aria-hidden />
        {denomination && state?.sellable
          ? `${denomination.name} can be bought in ${countryLabel}.`
          : `No catalogue issues for ${brandName} in ${countryLabel}.`}
      </section>
    );
  }

  return (
    <section
      aria-label="Catalogue checks"
      className="border-border flex flex-col gap-2 border-t px-4 py-3"
    >
      {denomination && state && itemBlocked && (
        <Callout
          tone="danger"
          title={
            !state.offered
              ? `${denomination.name} is not offered in ${countryLabel}`
              : `Customers cannot buy ${denomination.name} in ${countryLabel}`
          }
          action={openBrand}
        >
          {itemReasons.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {itemReasons.map((reason) => (
                <li key={reason}>
                  <StatusBadge variant="danger">{reasonLabel(reason)}</StatusBadge>
                </li>
              ))}
            </ul>
          )}
          <p className="text-muted-foreground">
            Checkout will not sell it until this is fixed on the brand’s denominations.
          </p>
        </Callout>
      )}

      {denomination && state && !itemBlocked && state.unusableProviders.length > 0 && (
        <Callout tone="warning" title="Some mapped providers cannot supply this item">
          <ul className="text-muted-foreground flex flex-col gap-0.5">
            {state.unusableProviders.map((provider) => (
              <li key={provider.vendorId}>
                <span className="text-foreground font-medium">
                  {provider.vendorName ?? `Provider ${provider.vendorId}`}
                </span>{' '}
                — {reasonLabel(provider.reason)}
              </li>
            ))}
          </ul>
          <p className="text-muted-foreground">
            Checkout still sells it through the chosen provider, with no fallback if that one fails.
          </p>
        </Callout>
      )}

      {issues.length > 0 && (
        <Callout
          tone={issues.some((issue) => issue.severity === 'danger') ? 'danger' : 'warning'}
          title={`${brandName} needs attention in ${countryLabel}`}
          action={itemBlocked ? undefined : openBrand}
        >
          <ul className="flex flex-col gap-1">
            {issues.map((issue) => (
              <li key={issue.key} className="flex items-center justify-between gap-2">
                <span>{issue.label}</span>
                {issue.count !== null && (
                  <StatusBadge variant={issue.severity} className="tabular-nums">
                    {issue.count}
                  </StatusBadge>
                )}
              </li>
            ))}
          </ul>
        </Callout>
      )}
    </section>
  );
}
